import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DocumentEntity, DocumentImportController, ImportFile, ImportMode } from '@domain';
import { RaButtonComponent, RaToastService } from '@shared/components';
import { finalize, forkJoin, switchMap } from 'rxjs';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { clampImportPercent } from 'src/app/modules/order/import-progress.util';

interface SelectedFile {
    file: File;
    error: string | null;
}

// Espelham o IMPORT_CONFIGS do backend (feedback imediato no front).
const MAX_FILES = 10;
const MAX_FILE_SIZE = 80 * 1024 * 1024; // 80 MB
const MAX_BATCH_SIZE = 300 * 1024 * 1024; // 300 MB
const MAX_MERGE_FILES = 5;

/** MIME suportados. `.doc` binário fica de fora (o backend/mammoth só lê .docx). */
const ALLOWED_MIME: Record<string, 'doc' | 'pdf' | 'xls' | 'ppt'> = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xls',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
};

/** Fallback por extensão quando o browser não preenche `file.type`. */
const EXT_TO_MIME: Record<string, string> = {
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

@Component({
    selector: 'import-documents-modal',
    templateUrl: 'import-documents-modal.component.html',
    styleUrl: 'import-documents-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        UpperCasePipe,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaIconComponent,
    ],
})
export class ImportDocumentsModalComponent {
    public orderId = input<string | null>(null);
    public close = output<void>();
    public created = output<DocumentEntity[]>();

    private _controller = inject(DocumentImportController);
    private _toast = inject(RaToastService);
    private _translate = inject(TranslateService);

    protected readonly maxFiles = MAX_FILES;
    protected readonly maxMergeFiles = MAX_MERGE_FILES;
    protected readonly acceptAttr = '.docx,.pdf,.xls,.xlsx,.pptx';

    protected readonly step = signal<'select' | 'mode'>('select');
    protected readonly files = signal<SelectedFile[]>([]);
    protected readonly mode = signal<ImportMode>('perFile');
    protected readonly isDragging = signal(false);
    protected readonly isProcessing = signal(false);
    protected readonly progressByName = signal<Record<string, number>>({});

    protected readonly validFiles = computed(() => this.files().filter(f => !f.error));
    protected readonly totalSize = computed(() =>
        this.validFiles().reduce((sum, f) => sum + f.file.size, 0)
    );
    protected readonly batchTooLarge = computed(() => this.totalSize() > MAX_BATCH_SIZE);
    protected readonly canMerge = computed(() => this.validFiles().length <= MAX_MERGE_FILES);
    protected readonly canImport = computed(
        () => this.validFiles().length > 0 && !this.batchTooLarge() && !this.isProcessing()
    );

    // #region [Seleção de arquivos]
    public onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) this._addFiles(input.files);
        input.value = '';
    }
    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragging.set(true);
    }
    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragging.set(false);
    }
    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragging.set(false);
        if (event.dataTransfer?.files) this._addFiles(event.dataTransfer.files);
    }

    public removeFile(index: number): void {
        this.files.set(this.files().filter((_, i) => i !== index));
    }

    private _addFiles(list: FileList): void {
        const next: SelectedFile[] = [...this.files()];
        for (const file of Array.from(list)) {
            // Dedupe por nome + tamanho.
            if (next.some(f => f.file.name === file.name && f.file.size === file.size)) continue;
            if (next.length >= MAX_FILES) {
                this._toast.warning({
                    message: this._t('order.documentImport.errors.tooManyFiles', { max: MAX_FILES }),
                });
                break;
            }
            next.push({ file, error: this._validate(file) });
        }
        this.files.set(next);
    }

    private _validate(file: File): string | null {
        if (!this._resolveMime(file)) return this._t('order.documentImport.errors.unsupportedType');
        if (file.size > MAX_FILE_SIZE)
            return this._t('order.documentImport.errors.fileTooLarge', { max: 80 });
        return null;
    }

    private _resolveMime(file: File): string | null {
        if (file.type && ALLOWED_MIME[file.type]) return file.type;
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        return EXT_TO_MIME[ext] ?? null;
    }
    // #endregion

    // #region [Navegação entre passos]
    public proceed(): void {
        if (this.validFiles().length > 1) {
            this.step.set('mode');
            return;
        }
        this.importNow();
    }
    public back(): void {
        this.step.set('select');
    }
    public selectMode(mode: ImportMode): void {
        if (mode === 'merge' && !this.canMerge()) return;
        this.mode.set(mode);
    }
    // #endregion

    // #region [Upload + geração]
    public importNow(): void {
        const valid = this.validFiles();
        if (!valid.length || this.batchTooLarge() || this.isProcessing()) return;

        // Se o modo é merge mas há arquivos demais, cai para perFile (proteção; o front
        // desabilita o merge nesse caso, mas garantimos aqui também).
        const mode: ImportMode = this.mode() === 'merge' && this.canMerge() ? 'merge' : 'perFile';

        this.isProcessing.set(true);
        this.progressByName.set({});

        forkJoin(
            valid.map(sf =>
                this._controller.uploadFile(sf.file, progress =>
                    this.progressByName.set({ ...this.progressByName(), [sf.file.name]: progress })
                )
            )
        )
            .pipe(
                switchMap((uploaded: ImportFile[]) =>
                    this._controller.generate(this.orderId(), mode, uploaded)
                ),
                finalize(() => this.isProcessing.set(false))
            )
            .subscribe({
                next: docs => {
                    this._toast.success({ message: this._t('order.documentImport.pending') });
                    this.created.emit(docs);
                    this.close.emit();
                },
                error: err => {
                    this._toast.error({ message: this._errorMessage(err) });
                    this.step.set('select');
                },
            });
    }
    // #endregion

    // #region [Helpers de template]
    protected fileKind(file: File): 'doc' | 'pdf' | 'xls' | 'ppt' | 'unknown' {
        const mime = this._resolveMime(file);
        return mime ? ALLOWED_MIME[mime] : 'unknown';
    }
    protected formatSize(bytes: number): string {
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    /** % do upload com o piso de 2% — barra vazia parada parece travada. */
    protected progressOf(name: string): number {
        return clampImportPercent(this.progressByName()[name] ?? 0);
    }
    // #endregion

    private _errorMessage(err: any): string {
        return (
            err?.error?.message?.[0] ||
            err?.message ||
            this._t('order.documentImport.errors.generic')
        );
    }
    private _t(key: string, params?: Record<string, unknown>): string {
        return this._translate.instant(key, params);
    }
}
